import type { CloudProvider, CloudMarketplace } from '../../types/index'
import type { UserState } from '../../types/external'
import type { ResaleAuthorizationsResellerAgreements } from '../../types/resaleAuthorization'
import type { links } from '../productsListing/types'
import type {
  Dimension,
  EulaType,
  Installments,
  MetaData,
  OfferRecipients,
  OfferStakeholders,
  OfferStatus,
  OfferTypeRenew,
  PriceTypes,
  ScheduleType,
} from '../privateOffer/types'

export enum ChannelPartnerPrivateOfferActionTypes {
  SET_CHANNEL_PARTNER_PRIVATE_OFFERS_DATA = 'SET_CHANNEL_PARTNER_PRIVATE_OFFERS_DATA',
  SET_CREATE_CHANNEL_OFFERS_DATA = 'SET_CREATE_CHANNEL_OFFERS_DATA',
  SET_SINGLE_CHANNEL_OFFER_DATA = 'SET_SINGLE_CHANNEL_OFFER_DATA',
  CLEAR_CHANNEL_OFFER_IN_CREATION = 'CLEAR_CHANNEL_OFFER_IN_CREATION',
  CLEAR_CHANNEL_OFFERS = 'CLEAR_CHANNEL_OFFERS',
}

export interface ResellerAgreementInCPPO {
  awsResellerAgreementId?: string
  agreementName?: string
  agreementDescription?: string
}

export interface CppoDimension extends Dimension {
  partnerMargin?: number | null
  cppoId?: string | null
}

export enum CppoPriceAdjustmentType {
  PERCENTAGE = 'discount',
  FIXED = 'markup',
}

export interface CppoStages {
  'Created at Labra'?: string | null
  Sent?: string | null
  Viewed?: string | null
  Cancelled?: string | null
  Accepted?: string | null
}

export enum CppoStageToStatusRelationMapEnum {
  IN_PROGRESS = 'Created at Labra',
  SENT = 'Sent',
  VIEWED = 'Viewed',
  CANCELLED = 'Cancelled',
  ACCEPTED = 'Accepted',
}

export type CppoAuditJson = {
  offerCreatedInLabraAt: string | null
  offerEmailSentAt: string | null
  offerViewedAt: string | null
  offerCancelledAt: string | null
  offerAcceptedAt: string | null
}

export type ChannelPartnerPrivateOfferInCreation = {
  companyName?: string
  awsAccountId?: string
  offerName?: string
  offerExpirationDate?: string | null
  subscriptionEndDate?: string | null
  offerDescription?: string
  offerRecipients?: OfferRecipients[]
  customEmailMessage?: string
  priceAdjustmentType?: CppoPriceAdjustmentType
  adjustmentPercentage?: number
  adjustmentPercentageInputValue?: number //to use for input before apply
  flexiblePaymentTerms?: boolean
  offerStakeholders?: OfferStakeholders[]
  customOfferStakeholders?: OfferStakeholders[]
  metaData?: MetaData[]
  installments?: Installments[]
  serviceLength?: number
  flexiblePaymentType?: ScheduleType
  dimensions?: CppoDimension[]
  variableStartDate?: boolean
  agreementStartDate?: string | null
  eulaType?: EulaType
  eulaFile?: string[]
  isResellerLicenseAgreementAccepted?: boolean
  draft?: boolean
  previousSubscriptionEndDate?: string
  firstInstallmentDate?: string | null
  labraOfferId?: string
}

export interface ChannelPartnerPrivateOffer {
  id: string
  companyName?: string
  awsAccountId?: string
  resaleAuthorizationId?: string
  offerStatus?: OfferStatus
  subscriptionEndDate?: string
  isvName?: string
  cloudProvider?: string | CloudProvider
  offerStakeholders?: OfferStakeholders[]
  customOfferStakeholders?: OfferStakeholders[]
  customEmailMessage?: string | null
  type?: string | OfferTypeRenew
  aboAgreementId?: string
  channelPartnerName?: string
  channelPartnerAccountId?: string
  offerName?: string
  offerDescription?: string
  offerExpirationDate?: string
  offerUrl?: string
  offerId?: string
  error?: string
  serviceLength?: number
  priceAdjustmentType?: CppoPriceAdjustmentType
  adjustmentPercentage?: number | null
  flexiblePaymentTerms?: boolean
  contractDuration?: number
  eulaType: EulaType
  eulaFile: string[]
  metaData?: MetaData[]
  offerRecipients?: OfferRecipients[]
  installments?: Installments[]
  awsProductId?: string
  draft?: boolean
  crmObjectId?: string
  cloudProductName?: string
  productType?: string
  createdAt?: string
  updatedAt?: string
  resellerAgreement?: ResellerAgreementInCPPO
  dimensions?: CppoDimension[] // prices are still unclear
  agreementStartDate?: string
  auditJson?: CppoAuditJson
  emailCustomMessage?: string
  cloudOfferId?: string
  labraSubscriptionId?: string
  creationOrigin?: string
  labraResaleAuthorizationId?: string
  labraOfferId?: string
  currency?: string
  cloudOfferUrl?: string
}

export type ChannelPartnerPrivateOffersApiResponse = {
  pageNumber?: number
  pageSize?: number
  totalCount?: number
  sortBy?: string
  sortOrder?: string
  links?: links
}

export type ApiResponse = ChannelPartnerPrivateOffersApiResponse

export type ChannelPartnerPrivateOffersState = {
  awsCppos: ChannelPartnerPrivateOffer[]
  count: number
  pageNumber: number
  noOfPage: number | null
  pageSize: number
  currentChannelPartnerPrivateOffer: {
    [x: string]: ChannelPartnerPrivateOffer
  }
  channelPartnerPrivateOfferInCreation: {
    [x: string]: ChannelPartnerPrivateOfferInCreation
  }
} & ChannelPartnerPrivateOffersApiResponse

export const initialChannelPartnerPrivateOffersState: ChannelPartnerPrivateOffersState =
  {
    awsCppos: [],
    count: 0,
    pageNumber: 0,
    noOfPage: null,
    pageSize: 10,
    currentChannelPartnerPrivateOffer: {},
    channelPartnerPrivateOfferInCreation: {},
  }

export type ChannelPartnerPrivateOffersAction =
  | {
      type: ChannelPartnerPrivateOfferActionTypes.SET_CHANNEL_PARTNER_PRIVATE_OFFERS_DATA
      payload: {
        awsCppos: ChannelPartnerPrivateOffer[]
        count: number
        pageNumber: number
        pageSize: number
      }
    }
  | {
      type: ChannelPartnerPrivateOfferActionTypes.SET_CREATE_CHANNEL_OFFERS_DATA
      payload: {
        resellerAuthId: string
        channelOffer: ChannelPartnerPrivateOfferInCreation
      }
    }
  | {
      type: ChannelPartnerPrivateOfferActionTypes.SET_SINGLE_CHANNEL_OFFER_DATA
      payload: {
        resellerAuthId: string
        channelOffer: ChannelPartnerPrivateOffer
      }
    }
  | {
      type: ChannelPartnerPrivateOfferActionTypes.CLEAR_CHANNEL_OFFER_IN_CREATION
      payload: string
    }
  | {
      type: ChannelPartnerPrivateOfferActionTypes.CLEAR_CHANNEL_OFFERS
    }

export type GetChannelPartnerPrivateOffersParams = {
  partnerId: string
  pageSize?: number
  pageNumber?: number
  crmOpportunityId?: string
  input?: string // for search functionality
  selectedInput?: string // for search functionality
  sectionName?: string // for search functionality
  selectedResellerAuth?: string
}

export type UpdateChannelPartnerPrivateOfferData = Record<string, unknown>

export type ChannelOfferUserProfile = {
  email?: string
  firstName?: string
  lastName?: string
}

export type GetInitialChannelOfferBasedOnCloudParams = {
  receivedResellerAuth?: ResaleAuthorizationsResellerAgreements
  cloudMarketplace: CloudMarketplace
  userProfile: ChannelOfferUserProfile
}

export type ChannelPartnerPrivateOffersListResponse = {
  aws_cppos?: unknown[]
  count: number
}

export type SingleChannelOfferApiResponse = {
  aws_cppos?: Array<Record<string, unknown>>
}

export type PostChannelOfferResponse = {
  cppo_id: string
}

export type GetChannelPartnerPrivateOfferParams = {
  cloudMarketplace: CloudMarketplace
  partnerId: string
  resellerAuthId: string
  channelOfferId: string
}

export type GetSingleChannelOfferBasedOnCloudParams = {
  cloudMarketplace: CloudMarketplace
  partnerId: string
  channelOfferId: string
  receivedResellerAuth?: ResaleAuthorizationsResellerAgreements
  allUsers?: UserState[]
}

export type GetAdjustedPercentageValueParams = {
  dimensions?: CppoDimension[]
  priceAdjustmentType: CppoPriceAdjustmentType
  adjustmentPercentage?: number | null
  resaleAuthorization?: ResaleAuthorizationsResellerAgreements
}

export type GetChannelPartnerPrivateOffersInCreationParams = {
  channelOfferId?: string
  resellerAuthId: string
  partnerId: string
}

export type SetChannelPartnerPrivateOfferInCreationParams = {
  resellerAuthId: string
  channelOffer: ChannelPartnerPrivateOfferInCreation
}

export type SetSingleChannelPartnerPrivateOfferParams = {
  resellerAuthId: string
  channelOffer: ChannelPartnerPrivateOfferInCreation
}

export type GetSendPayloadDataBasedOnCloudParams = {
  selectedCloud: CloudMarketplace
  channelOffer: ChannelPartnerPrivateOfferInCreation
  receivedResellerAuth?: ResaleAuthorizationsResellerAgreements
  saveAsDraft: boolean
}

export type GetSendCPPODimensionsParams = {
  channelOffer: ChannelPartnerPrivateOfferInCreation
  receivedResellerAuth?: ResaleAuthorizationsResellerAgreements
}

export type SendChannelOfferParams = {
  partnerId: string
  channelOfferId?: string
  saveAsDraft: boolean
  resellerAuthId: string
  channelOffer: ChannelPartnerPrivateOfferInCreation
  handleNext?: (id: string, draft: boolean) => void
}

export type SendReminderForCPPOParams = {
  privateOfferId: string
  data: Record<string, unknown>
  callBack?: () => void
  partnerId?: string
}

export type ResendCPPOParams = {
  privateOfferId: string
  data: Record<string, unknown>
  callBack?: () => void
  partnerId?: string
}

export type { PriceTypes, CloudMarketplace, MetaData }
